/**
 * AsseTracMap Component
 * Main map component using Mapbox GL and deck.gl
 * Renders icon-based asset markers, project boundaries, area overlays, and tooltips.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map, NavigationControl } from 'react-map-gl';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { useControl } from 'react-map-gl';
import { useResizeDetector } from 'react-resize-detector';
import { Box } from '@mui/material';

import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { updateViewport, setCurrentMapId, pushViewportStack, saveProjectMapState, setTooltip, clearTooltip } from '../../../store/slices/mapSlice';
import AssetTooltip from '../AssetTooltip';
import StructureCountBadges from './StructureCountBadges';

import useMapLayers from '../../../hooks/useMapLayers';

// DeckGL overlay component for react-map-gl
function DeckGLOverlay({ layers }) {
  const overlay = useControl(() => new MapboxOverlay({ interleaved: true }));
  
  useEffect(() => {
    overlay.setProps({ layers });
  }, [overlay, layers]);
  
  return null;
}

const NO_MARKERS = [];

const AsseTracMap = ({ onInitialize, visibleOverlays = [], showProjectMarkers = true, showBoundaryFill = true, areaElementMarkers = NO_MARKERS, areaElementShapes = null, structureBadges = NO_MARKERS }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  
  const { viewport, config, assetsData, projectBoundariesData, overlaysData, movesData } = useAppSelector(
    (state) => state.map
  );
  // ASS-81 Part B: needed to tag saveProjectMapState with the project this
  // snapshot belongs to.
  const currentProject = useAppSelector((state) => state.project.currentProject);
  const [initialized, setInitialized] = useState(false);
  const { ref: containerRef, width, height } = useResizeDetector();

  function handleAssetClick(info) {
    if (info.object) {
      const assetId = info.object.id;
      dispatch(saveProjectMapState(currentProject?.id));
      dispatch(pushViewportStack(viewport));
      navigate(`/asset/${assetId}`);
    }
  }

  // Handle project click - navigate to project page
  function handleProjectClick(info) {
    if (info.object) {
      const project = info.object;
      dispatch(pushViewportStack(viewport));
      navigate(`/projects/${project.id}`);
    }
  }

  // Handle project hover for tooltip
  const handleProjectHover = useCallback((info) => {
    if (info.object) {
      dispatch(
        setTooltip({
          x: info.x,
          y: info.y,
          type: 'project',
          name: info.object.name,
          client: info.object.client,
          status: info.object.status,
        })
      );
    } else {
      dispatch(clearTooltip());
    }
  }, [dispatch]);

  // Get map layers from custom hook
  const { allLayers } = useMapLayers({
    assetsData,
    projectBoundariesData,
    overlaysData,
    movesData,
    visibleOverlays,
    showProjectMarkers,
    showBoundaryFill,
    areaElementMarkers,
    areaElementShapes,
    onAssetClick: handleAssetClick,
    onProjectClick: handleProjectClick,
    onProjectHover: handleProjectHover,
  });

  // Initialize map when container is ready
  useEffect(() => {
    if (width && height && !initialized) {
      dispatch(updateViewport({ width, height }));
      setInitialized(true);
      
      if (typeof onInitialize === 'function') {
        onInitialize();
      }
    }
  }, [width, height, initialized, dispatch, onInitialize]);

  // Update viewport on resize
  useEffect(() => {
    if (initialized && width && height) {
      dispatch(updateViewport({ width, height }));
    }
  }, [width, height, initialized, dispatch]);

  // Handle map move
  const handleMove = useCallback((evt) => {
    dispatch(updateViewport(evt.viewState));
  }, [dispatch]);

  // Handle map load
  const handleMapLoad = useCallback(() => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      dispatch(setCurrentMapId(map._mapId));
    }
  }, [dispatch]);

  // Handle mouse leave to clear tooltip
  const handleMouseLeave = useCallback(() => {
    dispatch(clearTooltip());
  }, [dispatch]);

  // Check if Mapbox token is configured
  if (!config.mapboxApiAccessToken) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.paper',
          color: 'text.secondary',
          p: 4,
          textAlign: 'center',
        }}
      >
        <div>
          <h3>Mapbox Token Required</h3>
          <p>Please set VITE_MAPBOX_TOKEN in your environment variables.</p>
        </div>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      onMouseLeave={handleMouseLeave}
      sx={{
        height: '100%',
        width: '100%',
        position: 'relative',
      }}
    >
      {initialized && (
        <Map
          ref={mapRef}
          mapboxAccessToken={config.mapboxApiAccessToken}
          mapStyle={config.mapStyle}
          {...viewport}
          onMove={handleMove}
          onLoad={handleMapLoad}
          onMouseOut={handleMouseLeave}
          style={{ width: '100%', height: '100%' }}
          attributionControl={true}
          reuseMaps
        >
          <DeckGLOverlay layers={allLayers} />
          {/* AST-89: HTML markers, so they sit above the deck.gl canvas */}
          {structureBadges.length > 0 && <StructureCountBadges badges={structureBadges} />}
          <NavigationControl
            position="bottom-right"
            showZoom
            showCompass={false}
          />
        </Map>
      )}
      
      {/* Asset / Area hover tooltip */}
      <AssetTooltip />
    </Box>
  );
};

export default AsseTracMap;
